import { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  Calendar,
  Clock,
  ClipboardCheck,
  FileCheck2,
  FileText,
  GraduationCap,
  ListChecks,
  MessageSquare,
  ShieldCheck,
  Upload,
} from 'lucide-react';
import { useDialog } from '@gokrew/ui';
import { formatDate } from '@gokrew/ui/utility/utility';
import { cn } from '@gokrew/ui/lib/utils';
import { TranslationNS } from '@gokrew/models/constants';
import { useContinueSigning } from '@/hooks/useSignatureRequests';
import { EmployeeTaskDetailDialog } from '@/pages/tasks/EmployeeTaskDetailDialog';
import { ApprovalDetails } from '@/pages/my-team/approvals/ApprovalDetails';
import type { WorkspaceAction } from '@/types/workspace';
import styles from './WorkspaceActionRow.module.css';

const ICONS = {
  task: ListChecks,
  survey: MessageSquare,
  signature: FileCheck2,
  acknowledgement: FileText,
  upload: Upload,
  training: GraduationCap,
  review: ClipboardCheck,
  approval: Calendar,
  document_approval: ShieldCheck,
};

export const WorkspaceActionRow = memo(
  ({ item }: { item: WorkspaceAction }) => {
    const { t, i18n } = useTranslation(TranslationNS.EmployeeTasks);
    const navigate = useNavigate();
    const { openDialog } = useDialog();
    const signing = useContinueSigning();
    const Icon = ICONS[item.kind];
    const open = () => {
      if (item.kind === 'signature') signing.mutate(item.id);
      else if (item.kind === 'task')
        openDialog({
          title: t('tasks.taskDetails'),
          containerClass: 'w-full md:w-3xl',
          content: <EmployeeTaskDetailDialog assignmentId={item.id} />,
        });
      else if (item.request)
        openDialog({
          title: t('workspace.review_request', {
            name: item.request.employeeName,
          }),
          containerClass: styles.approvalDialog,
          content: <ApprovalDetails request={item.request} withActions />,
        });
      else if (item.href) navigate(item.href);
    };
    return (
      <button
        type="button"
        className={styles.row}
        onClick={open}
        disabled={signing.isPending}
        data-action-kind={item.kind}
      >
        <span className={cn(styles.icon, item.overdue && styles.overdueIcon)}>
          <Icon aria-hidden />
        </span>
        <span className={styles.title} title={item.title}>
          {item.title}
        </span>
        <span className={styles.kind}>{t(`workspace.kind.${item.kind}`)}</span>
        <span className={cn(styles.due, item.overdue && styles.overdue)}>
          {item.overdue && <Clock aria-hidden />}
          {item.due && (
            <>
              <span className="sr-only">
                {t(item.overdue ? 'workspace.overdue' : 'workspace.due')}
              </span>
              <time dateTime={item.due}>
                {formatDate(`${item.due.slice(0, 10)}T12:00:00`, {
                  format: 'D MMM',
                  locale: i18n.language,
                })}
              </time>
            </>
          )}
        </span>
        <ArrowRight className={styles.arrow} aria-hidden />
      </button>
    );
  }
);
WorkspaceActionRow.displayName = 'WorkspaceActionRow';
